const beforeInputHandler = ({ originalValue, inputType, caretPosition, selectionLength = 0, addedChar = "" }) => {
    let newOriginalValue = originalValue;
    if (inputType === "insertText") {
        // handle insertion of new character
        if (selectionLength > 0) {
            // replace selected text
            newOriginalValue =
                originalValue.slice(0, caretPosition) +
                    addedChar +
                    originalValue.slice(caretPosition + selectionLength);
        }
        else {
            // insert new character at caret position
            newOriginalValue =
                originalValue.slice(0, caretPosition) +
                    addedChar +
                    originalValue.slice(caretPosition);
        }
    }
    else if (inputType === "deleteContentBackward") {
        // handle deletion backward (Backspace)
        if (selectionLength > 0) {
            // remove selected text
            newOriginalValue =
                originalValue.slice(0, caretPosition) +
                    originalValue.slice(caretPosition + selectionLength);
        }
        else if (caretPosition > 0) {
            // delete character before caret
            newOriginalValue =
                originalValue.slice(0, caretPosition - 1) +
                    originalValue.slice(caretPosition);
        }
    }
    else if (inputType === "deleteContentForward") {
        // handle deletion forward (Delete)
        if (selectionLength > 0) {
            // remove selected text
            newOriginalValue =
                originalValue.slice(0, caretPosition) +
                    originalValue.slice(caretPosition + selectionLength);
        }
        else if (caretPosition < originalValue.length) {
            // delete character after caret
            newOriginalValue =
                originalValue.slice(0, caretPosition) +
                    originalValue.slice(caretPosition + 1);
        }
    }
    // adjust caret position
    let newCaretPosition = caretPosition;
    if (inputType === "insertText") {
        newCaretPosition = caretPosition + 1;
    }
    else if (inputType === "deleteContentBackward") {
        newCaretPosition = selectionLength > 0 ? caretPosition : caretPosition - 1;
    }
    else if (inputType === "deleteContentForward") {
        newCaretPosition = caretPosition;
    }
    // ensure the caret stays within valid bounds
    newCaretPosition = Math.max(0, Math.min(newCaretPosition, newOriginalValue.length));
    return { newOriginalValue, newCaretPosition };
};

const pasteHandler = ({ originalValue, caretPosition, pastedText, selectionLength = 0, }) => {
    if (selectionLength > 0) {
        // remove the selected text from originalValue
        originalValue = originalValue.slice(0, caretPosition) + originalValue.slice(caretPosition + selectionLength);
    }
    // insert the pasted text at the caret position, and adjust originalValue
    const newOriginalValue = originalValue.slice(0, caretPosition) +
        pastedText +
        originalValue.slice(caretPosition);
    const newCaretPosition = caretPosition + pastedText.length;
    return {
        newOriginalValue,
        newCaretPosition
    };
};

const applyMaskedInput = (inputElement, config = {}) => {
    var _a;
    inputElement.setAttribute("autocomplete", "off");
    inputElement.setAttribute("type", "text");
    inputElement.setAttribute("autocorrect", "off");
    inputElement.setAttribute("spellcheck", "false");
    let originalValue = "";
    const character = (_a = config.character) !== null && _a !== void 0 ? _a : "•";
    let activeEvent = true;
    const getOriginalValue = () => {
        return activeEvent ? originalValue : String(inputElement.value);
    };
    const getCaretPosition = (el) => {
        var _a;
        return (_a = el.selectionStart) !== null && _a !== void 0 ? _a : 0;
    };
    const getSelectionLength = (el) => {
        var _a, _b;
        return ((_a = el.selectionEnd) !== null && _a !== void 0 ? _a : 0) - ((_b = el.selectionStart) !== null && _b !== void 0 ? _b : 0);
    };
    const setCaretPosition = (el, position) => {
        el.setSelectionRange(position, position);
    };
    const onChangeValue = () => {
        if (typeof config.onChange === "function") {
            config.onChange(getOriginalValue());
        }
    };
    // handle pasting text
    const handlePaste = (e) => {
        var _a, _b;
        // get the pasted text
        const pastedText = (_b = (_a = e.clipboardData) === null || _a === void 0 ? void 0 : _a.getData("text")) !== null && _b !== void 0 ? _b : "";
        const caretPosition = getCaretPosition(inputElement);
        const selectionLength = getSelectionLength(inputElement);
        e.preventDefault(); // prevent default paste behavior
        const { newCaretPosition, newOriginalValue } = pasteHandler({
            originalValue,
            caretPosition,
            pastedText,
            selectionLength
        });
        originalValue = newOriginalValue;
        // mask the value
        inputElement.value = character.repeat(originalValue.length);
        // move caret to end of pasted text
        setCaretPosition(inputElement, newCaretPosition);
        onChangeValue();
    };
    const handleBeforeInput = (e) => {
        const caretPosition = getCaretPosition(inputElement);
        const selectionLength = getSelectionLength(inputElement);
        const inputType = e.inputType;
        const addedChar = e.data || "";
        const { newCaretPosition, newOriginalValue } = beforeInputHandler({
            originalValue,
            inputType,
            caretPosition,
            selectionLength,
            addedChar
        });
        originalValue = newOriginalValue;
        e.preventDefault(); // prevent default behavior as we are handling the value
        // mask the value
        inputElement.value = character.repeat(originalValue.length);
        // restore caret position
        setCaretPosition(inputElement, newCaretPosition);
        onChangeValue();
    };
    const handleEventOnDestroy = () => {
        onChangeValue();
    };
    // add events
    inputElement.addEventListener("paste", handlePaste);
    inputElement.addEventListener("beforeinput", handleBeforeInput);
    return {
        getOriginalValue: () => getOriginalValue(),
        destroy: () => {
            if (!activeEvent) {
                return;
            }
            inputElement.removeEventListener("paste", handlePaste);
            inputElement.removeEventListener("beforeinput", handleBeforeInput);
            if (typeof config.onChange === "function") {
                inputElement.addEventListener("paste", handleEventOnDestroy);
                inputElement.addEventListener("input", handleEventOnDestroy);
            }
            inputElement.value = originalValue;
            activeEvent = false;
        },
        addEvent: () => {
            if (activeEvent) { // handle duplicate events                
                return;
            }
            if (typeof config.onChange === "function") {
                inputElement.removeEventListener("paste", handleEventOnDestroy);
                inputElement.removeEventListener("input", handleEventOnDestroy);
            }
            activeEvent = true;
            inputElement.addEventListener("paste", handlePaste);
            inputElement.addEventListener("beforeinput", handleBeforeInput);
            originalValue = inputElement.value;
            inputElement.value = character.repeat(originalValue.length);
            setCaretPosition(inputElement, originalValue.length);
        },
        purgeDestroy: () => {
            if (activeEvent) {
                inputElement.removeEventListener("paste", handlePaste);
                inputElement.removeEventListener("beforeinput", handleBeforeInput);
            }
            else {
                inputElement.removeEventListener("paste", handleEventOnDestroy);
                inputElement.removeEventListener("input", handleEventOnDestroy);
            }
            inputElement.value = originalValue;
            activeEvent = false;
        }
    };
};

export { applyMaskedInput };
